import { addDays, format } from 'date-fns';
import type { EnablementRow } from './enablementRows';
import type { QuarterBounds } from './fiscalQuarters';

const iso = (d: Date) => format(d, 'yyyy-MM-dd');

/** A believable fiscal calendar around today (13-week quarters) for previews when Salesforce isn't reachable. */
export function demoQuarterBounds(today: Date = new Date()): QuarterBounds {
  const curEnd = addDays(today, 62);
  return {
    current: { start: iso(addDays(today, -29)), end: iso(curEnd) },
    next: { start: iso(addDays(curEnd, 1)), end: iso(addDays(curEnd, 91)) },
    yearEnd: iso(addDays(curEnd, 91 * 2)),
  };
}

/** Seventeen enablement rows (journey maps and Mindtickle series) in every state, for layout previews. */
export function demoEnablementRows(today: Date = new Date()): EnablementRow[] {
  const back = (n: number | null) => (n == null ? null : iso(addDays(today, -n)));
  const ahead = (n: number | null) => (n == null ? null : iso(addDays(today, n)));
  const jm = (n: number, name: string, status: EnablementRow['status'], percent: number, steps: number, mins: number | null, due: number | null, kind: 'due' | 'expires', doneAgo: number | null): EnablementRow => ({
    id: `jm-demo-${n}`,
    source: 'journey',
    name,
    detail: '',
    required: n !== 6,
    status,
    percent,
    progress: `${Math.round((percent / 100) * steps)} of ${steps} steps${n % 2 ? ' · pass at 80%' : ''}`,
    minutesTotal: mins,
    minutesLeft: mins == null ? null : status === 'completed' ? 0 : Math.round((mins * (100 - percent)) / 100),
    deadline: ahead(due),
    deadlineKind: due == null ? null : kind,
    completedOn: back(doneAgo),
    href: `https://cyberacademy.cisco.com/trainings/${900 + n}`,
    external: true,
  });
  const mt = (n: number, name: string, label: string | null, status: EnablementRow['status'], percent: number, mods: number, win: string | null, req: boolean, due: number | null, doneAgo: number | null): EnablementRow => ({
    id: `mt-demo-${n}`,
    source: 'mindtickle',
    name,
    detail: [label, win].filter(Boolean).join(' · '),
    required: req,
    status,
    percent,
    progress: `${Math.round((percent / 100) * mods)} of ${mods} ${req ? 'required ' : ''}modules`,
    minutesTotal: null,
    minutesLeft: null,
    deadline: ahead(due),
    deadlineKind: due == null ? null : 'due',
    completedOn: back(doneAgo),
    href: `https://cisco.mindtickle.com/new/ui/learner/training/programs/${n}`,
    external: true,
  });
  return [
    jm(1, 'Geo Foundational - 0-30 Days', 'completed', 100, 12, 240, -20, 'due', 41),
    jm(2, 'Geo Foundational - 31-60 Days', 'completed', 100, 9, 180, -6, 'due', 12),
    jm(3, 'Geo Foundational - 61-90 Days', 'in_progress', 56, 9, 195, 18, 'due', null),
    jm(4, 'Secure Access Foundations', 'in_progress', 25, 8, 150, 75, 'expires', null),
    jm(5, 'SASE Design Practitioner', 'not_started', 0, 10, 300, 140, 'due', null),
    jm(6, 'Customer Discovery Essentials', 'completed', 100, 6, 90, null, 'due', 88),
    mt(1, 'FY27 Q1 Security Portfolio Update', 'QEP', 'completed', 100, 5, 'This quarter', true, -4, 19),
    mt(2, 'FY27 Q2 Security Portfolio Update', 'QEP', 'in_progress', 60, 5, 'This quarter', true, 62, null),
    mt(3, 'Zero Trust Architecture Deep Dive', null, 'in_progress', 33, 6, 'This fiscal year', true, 215, null),
    mt(4, 'XDR Technical Certification', 'Certification', 'completed', 100, 8, null, true, null, 63),
    mt(5, 'Firewall Threat Defense Essentials', null, 'not_started', 0, 7, 'This fiscal year', true, 215, null),
    mt(6, 'Identity Services Engine Workshop', null, 'not_started', 0, 4, 'This quarter', true, 62, null),
    mt(7, 'Email Security Pitch Skills', 'Selling', 'completed', 100, 3, null, false, null, 7),
    mt(8, 'Competitive Positioning Lab', 'Selling', 'in_progress', 80, 5, null, false, null, null),
    mt(9, 'Cloud Security Fundamentals', null, 'not_started', 0, 6, null, false, null, null),
    mt(10, 'Splunk for SEs', null, 'completed', 100, 4, null, false, null, 120),
    mt(11, 'Executive Briefing Storytelling', 'Selling', 'not_started', 0, 3, 'This fiscal year', false, 215, null),
  ];
}
